import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  Input,
  OnChanges,
  Output,
  Renderer2,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatCheckboxChange } from '@cdw-ae/common-frontend-materials';

@Component({
  selector: 'lib-cdw-checkbox',
  templateUrl: './cdw-checkbox.component.html',
  styleUrls: ['./cdw-checkbox.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CdwCheckboxComponent),
      multi: true,
    },
  ],
  standalone: false,
})
export class CdwCheckboxComponent
  implements ControlValueAccessor, AfterViewInit, OnChanges
{
  constructor(
    private changDetector: ChangeDetectorRef,
    private renderer: Renderer2,
    private elementRef: ElementRef
  ) {}

  @ViewChild(MatCheckbox) private matCheckbox!: MatCheckbox;

  @Input() indeterminate = false;

  @Input() disabled = false;

  @Input() checked = false;

  @Input() value!: unknown;

  @Input() color: 'primary' | 'accent' | 'warn' = 'primary'; // Material color theme

  @Input() checkmarkColor?: string; // Custom checkmark color (overrides theme)

  @Input() fillColor?: string; // Custom checkbox fill/background color when checked

  @Input() borderColor?: string; // Custom checkbox border color

  @Output() checkedEmitter: EventEmitter<unknown> = new EventEmitter();

  onChange = (value: boolean) => {
    // Callback placeholder - will be replaced by registerOnChange
  };

  onTouched = () => {
    // Callback placeholder - will be replaced by registerOnTouched
  };

  registerOnChange(fn: () => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  writeValue(value: boolean) {
    this.checked = value;
  }

  onCheckboxChanged($event: MatCheckboxChange) {
    this.checked = $event.checked;
    this.onChange($event.checked);
    this.onTouched();
    this.checkedEmitter.emit({
      checkboxValue: this.value,
      checked: $event.checked,
    });
  }

  setDisabledState(isDisabled: boolean) {
    this.disabled = isDisabled;
  }

  check() {
    this.checked = true;
    if (this.matCheckbox) {
      this.matCheckbox.checked = true;
      this.syncIndeterminate();
    }
    this.changDetector.detectChanges();
  }

  uncheck() {
    this.checked = false;
    if (this.matCheckbox) {
      this.matCheckbox.checked = false;
      this.syncIndeterminate();
    }
    this.changDetector.detectChanges();
  }

  // MatCheckbox's click handler on an indeterminate checkbox queues a microtask
  // that resets `_indeterminate = false` and writes `input.indeterminate = false`
  // AFTER the synchronous `change` event fires. A direct write here would be
  // undone by that microtask. Queueing our own microtask ensures it drains
  // after MatCheckbox's (FIFO order), restoring the intended indeterminate
  // state on the next CD cycle.
  private syncIndeterminate() {
    if (!this.matCheckbox) return;
    this.matCheckbox.indeterminate = this.indeterminate;
    queueMicrotask(() => {
      if (this.matCheckbox) {
        this.matCheckbox.indeterminate = this.indeterminate;
        this.changDetector.detectChanges();
      }
    });
  }

  ngAfterViewInit(): void {
    this.applyCustomStyles();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      'checkmarkColor' in changes ||
      'fillColor' in changes ||
      'borderColor' in changes
    ) {
      // Delay to ensure DOM is ready
      setTimeout(() => this.applyCustomStyles(), 0);
    }
  }

  private applyCustomStyles(): void {
    if (!this.elementRef) return;

    const nativeElement = this.elementRef.nativeElement;

    // Apply checkmark color
    if (this.checkmarkColor) {
      const checkmarkPaths = nativeElement.querySelectorAll(
        '.mdc-checkbox__checkmark-path, .mat-checkbox-checkmark-path, svg path'
      );
      checkmarkPaths.forEach((path: HTMLElement) => {
        this.renderer.setStyle(path, 'stroke', this.checkmarkColor);
      });
    }

    // Apply fill color
    if (this.fillColor) {
      const backgrounds = nativeElement.querySelectorAll(
        '.mdc-checkbox__background, .mat-checkbox-background'
      );
      backgrounds.forEach((bg: HTMLElement) => {
        this.renderer.setStyle(bg, 'background-color', this.fillColor);
      });
    }

    // Apply border color
    if (this.borderColor) {
      const frames = nativeElement.querySelectorAll(
        '.mdc-checkbox__background, .mat-checkbox-frame'
      );
      frames.forEach((frame: HTMLElement) => {
        this.renderer.setStyle(frame, 'border-color', this.borderColor);
      });
    }
  }
}
